import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (CFT160/CFT161/CFT162/CFT163/CFT164)
// ============================================================

export interface CFT160 {
  ft160_01: string;        // Meeting code (PK)
  ft160_11: string;        // Meeting subject
  ft160_12: string;        // Description (memo)
  ft160_13: string | null; // Meeting date (ISO string)
  ft160_14: string;        // Start time
  ft160_15: string;        // End time
  ft160_16: string;        // Meeting location
  ft160_17: string;        // Chairman code
  ft160_18: number;        // Internal attendee count
  ft160_19: number;        // Agenda item count
  ft160_20: string;        // Chairman name
  ft160_21: string;        // Recorder code
  ft160_22: string;        // Recorder name / Creator ID
  ft160_24: string;        // Related code
  ft160_25: number;        // Customer attendee count
  ft160_30: string;        // Transferred formal code
  ft160_91: string;        // Modified by
  ft160_92: string | null; // Modified at
  ft160_93: string;        // Created by

  // Associations
  attendees?: CFT161[];
  agendas?: CFT162[];
  customer_people?: CFT164[];
}

export interface CFT161 {
  ft161_01: string;        // Meeting code (FK)
  ft161_02: number;        // Sequence no
  ft161_11: string;        // Employee code
  ft161_11a: string;       // Chinese name
  ft161_11b: string;       // English name
  ft161_12: string;        // Department code
  ft161_12a: string;       // Department name
  ft161_13: string;        // Reserved
  ft161_14: string;        // Reserved
  ft161_91: string;        // Modified by
  ft161_92: string | null; // Modified at
  ft161_93: string;        // Created by
}

export interface CFT162 {
  ft162_01: string;        // Meeting code (FK)
  ft162_02: number;        // Sequence no
  ft162_11: string;        // Discussion content (memo)
  ft162_12: string;        // Resolution (memo)
  ft162_91: string;        // Modified by
  ft162_92: string | null; // Modified at
  ft162_93: string;        // Created by
  ft162_21: number;        // Tracking item count

  // Sub-detail: tracking items
  tracking_items?: CFT163[];
}

export interface CFT163 {
  ft163_01: string;        // Meeting code (FK)
  ft163_02: number;        // Agenda sequence (FK)
  ft163_03: number;        // Tracking item sequence
  ft163_11: string;        // Tracking item description
  ft163_12: string | null; // Expected completion date
  ft163_13: string;        // Status / handling notes (memo)
  ft163_14: string | null; // Actual completion date
  ft163_15: string;        // Responsible person code
  ft163_15a: string;       // Responsible person name
  ft163_16: string;        // Department code
  ft163_16a: string;       // Department name
}

export interface CFT164 {
  ft164_01: string;        // Meeting code (FK)
  ft164_02: number;        // Sequence no
  ft164_10: string;        // Name
  ft164_11: string;        // Title
  ft164_12: string;        // Department
  ft164_13: string;        // Customer code
  ft164_14: string;        // Customer name
  ft164_91: string;        // Modified by
  ft164_92: string | null; // Modified at
  ft164_93: string;        // Created by
}

// ============================================================
// Request / Response types
// ============================================================

export interface T20AListResponse {
  data: CFT160[];
  total: number;
  page: number;
  size: number;
}

export interface T20ACreateRequest {
  master: Partial<CFT160>;
  attendees: Partial<CFT161>[];
  agendas: Partial<CFT162>[];
  customer_people: Partial<CFT164>[];
}

// ============================================================
// API methods
// ============================================================

export const t20aApi = {
  async list(page = 1, size = 20): Promise<T20AListResponse> {
    const res = await apiClient.get('/api/t20a', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<CFT160> {
    const res = await apiClient.get(`/api/t20a/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: T20ACreateRequest): Promise<CFT160> {
    const res = await apiClient.post('/api/t20a', data);
    return res.data;
  },

  async update(id: string, data: T20ACreateRequest): Promise<CFT160> {
    const res = await apiClient.put(`/api/t20a/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/t20a/${encodeURIComponent(id)}`);
  },

  /** Shared lookup -- resolve display name for a reference code */
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
